import { useEffect, useState, ChangeEvent, useRef, useLayoutEffect } from "react"
import PropTypes from "prop-types"
import EncircledXIcon from "shared/components/icons/encircled_x_icon"

import SectionHeader from "./section_header"

const HOURS = ["00", "01", "02", "03"]
const MINUTES = ["00", "01", "02", "03", "04", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"]
const SECONDS = ["00", "15", "30", "45"]

export default function Delay({ defaultDelay, canDelay, onChange: parentOnChange, proPlusSubscriptionUrl, enabled }) {
  const fieldsetClass = "composer-add-field fieldset composer-fieldset"
  const disabledFieldsetClass = `${fieldsetClass} composer-fieldset-disabled`
  const [totalDelay, setTotalDelay] = useState(defaultDelay)
  const firstUpdate = useRef(true)

  const [selectedHours, updateSelectedHours] = useState<string>("00")
  const [selectedMinutes, updateSelectedMinutes] = useState<string>("00")
  const [selectedSeconds, updateSelectedSeconds] = useState<string>("15")
  const time = {
    Hour: { list: HOURS, selection: selectedHours },
    Minutes: { list: MINUTES, selection: selectedMinutes },
    Seconds: { list: SECONDS, selection: selectedSeconds },
  }

  const onChange = newDelay => {
    setTotalDelay(newDelay)
    parentOnChange(newDelay)
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    if (name === "Hour") {
      updateSelectedHours(value)
      if (value === "03") {
        updateSelectedMinutes("00")
        updateSelectedSeconds("00")
      }
    } else if (name === "Minutes") {
      updateSelectedMinutes(value)
    } else {
      updateSelectedSeconds(value)
    }
  }

  useEffect(() => {
    if (!firstUpdate.current) {
      let newTotalDelay = 3600 * Number(selectedHours) + 60 * Number(selectedMinutes) + Number(selectedSeconds)
      if (newTotalDelay == 0) {
        updateSelectedSeconds("15")
        newTotalDelay = 15
      }
      onChange(newTotalDelay)
    }

    firstUpdate.current = false
  }, [selectedHours, selectedMinutes, selectedSeconds, firstUpdate])

  if (!enabled) {
    return (
      <div className={disabledFieldsetClass}>
        <div className="composer-add-field field">
          <input
            disabled={true}
            type="button"
            className="button-tertiary"
            value="Add delay"
            title="Add a trigger first"
          />

          <div className="composer-add-field-message">
            <b>Add a trigger to unlock adding an action delay.</b>
          </div>
        </div>
      </div>
    )
  }

  if (Number(totalDelay) === totalDelay) {
    return (
      <div className={fieldsetClass}>
        <div className="partners-tanda-composer field">
          <SectionHeader
            title="Delay"
            note=""
            actions={
              <a onClick={() => onChange(null)} className="partners-tanda-composer-edit-link">
                <span>
                  <EncircledXIcon /> Remove delay
                </span>
              </a>
            }
          />

          <div className="delay-action">
            <div className="delay-action-editor">
              <div className="delay-action-container">
                <form className="delay-action-form" id="delay-action-form">
                  {Object.entries(time).map(([key, value]) => (
                    <div key={key} className="input">
                      <label htmlFor={key} className="label">
                        {key}
                      </label>
                      <div>
                        <select
                          name={key}
                          value={value.selection}
                          onChange={handleInputChange}
                          disabled={selectedHours == "03" && key !== "Hour"}
                        >
                          {value.list.map(value => (
                            <option
                              disabled={
                                selectedHours == "00" && selectedMinutes == "00" && key == "Seconds" && value == "00"
                              }
                              key={value}
                              value={value}
                            >
                              {value}
                            </option>
                          ))}
                        </select>
                        {key === "Seconds" ? null : <div className="separator">:</div>}
                      </div>
                    </div>
                  ))}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={canDelay ? fieldsetClass : disabledFieldsetClass}>
      <div className="field">
        <input
          onClick={() => onChange(15)}
          disabled={!canDelay}
          type="button"
          className="button-tertiary"
          value="Add delay"
        />
      </div>
      {!canDelay && (
        <p className="composer-add-field-message">
          <b>Delay is a Pro+ feature.</b>{" "}
          <a href={proPlusSubscriptionUrl} target="_blank" rel="noopener noreferrer" className="link-inline">
            Upgrade
          </a>{" "}
          to use action delays and more in your Applets.
        </p>
      )}
    </div>
  )
}

Delay.propTypes = {
  canDelay: PropTypes.bool.isRequired,
  defaultDelay: PropTypes.number,
  enabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  proPlusSubscriptionUrl: PropTypes.string.isRequired,
}
